import type { FC } from 'react';
import React, { useEffect, useMemo } from 'react';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { useExperimentalMacroMetadataQuery } from '@confluence/excerpt-include/entry-points/useExperimentalMacroMetadataQuery.ts';
import { isMigratedMacroImplEnabled } from '@confluence/macro-utils/entry-points/evaluateMacroEnablement.ts';
import { parseNestedAdfDocument } from '@confluence/macro-utils/entry-points/nestedAdfRenderer.ts';
import { getLogger } from '@confluence/logger';
import { usePageIncludeMacroQuery } from '@confluence/page-include-macro/entry-points/usePageIncludeMacroQuery.ts';
import { parseMacroTarget } from '@confluence/page-include-macro/entry-points/pageIncludeMacroParams.ts';
import type { PageIncludeMacroParams } from '@confluence/page-include-macro/entry-points/PageIncludeMacroTypes.ts';

import { extractExcerptContent, type IncludedContentReference } from './handleADF.ts';

const logger = getLogger('table-of-contents');

export type IncludedContentEntry = {
	key: string;
	content: ADFEntity[];
};

type SubscriberProps = {
	reference: IncludedContentReference;
	onResolve: (entry: IncludedContentEntry) => void;
};

const parseAdfPayload = (
	payload: string | undefined | null,
): ADFEntity | ADFEntity[] | undefined => {
	if (!payload) {
		return undefined;
	}
	try {
		return JSON.parse(payload) as ADFEntity | ADFEntity[];
	} catch (error) {
		logger.error`Failed to parse included content payload for TOC - ${error}`;
		return undefined;
	}
};

/**
 * Subscribes to the same GraphQL query used by ExcerptIncludeImpl
 * (useExperimentalMacroMetadataQuery). Because that hook keys by JSON.stringify
 * of variables and uses an internal shared cache, we get the macro's result
 * without firing an extra request.
 */
const ExcerptIncludeTocSubscriber: FC<SubscriberProps> = ({ reference, onResolve }) => {
	const variables = useMemo(
		() => ({
			contentId: reference.parentContentId ?? '',
			macroNode: {
				extensionKey: 'excerpt-include',
				extensionType: reference.extensionType ?? '',
				parameters: {
					macroMetadata: JSON.stringify(reference.macroMetadata ?? {}),
					macroParams: JSON.stringify(reference.macroParams ?? {}),
				},
				type: reference.nodeType ?? 'extension',
			},
		}),
		[reference],
	);

	const { data, loading, error } = useExperimentalMacroMetadataQuery({
		variables,
		skip: !reference.parentContentId,
	});

	useEffect(() => {
		if (loading) {
			return;
		}
		if (error) {
			onResolve({ key: reference.key, content: [] });
			return;
		}
		const parsed = parseAdfPayload(data?.experimentalMacroMetadata?.payload);
		if (!parsed) {
			onResolve({ key: reference.key, content: [] });
			return;
		}
		// The excerpt-include payload comes back already resolved as an array of
		// ADF content nodes (the excerpt's body content). Older callers may still
		// receive a full `doc` shape, so support both.
		let content: ADFEntity[];
		if (Array.isArray(parsed)) {
			content = parsed as ADFEntity[];
		} else if (parsed.type === 'doc' && Array.isArray((parsed as ADFEntity).content)) {
			content = extractExcerptContent(parsed, reference.excerptName);
		} else if (Array.isArray((parsed as ADFEntity).content)) {
			content = ((parsed as ADFEntity).content as ADFEntity[]) ?? [];
		} else {
			content = [];
		}
		onResolve({ key: reference.key, content });
	}, [data, loading, error, reference.key, reference.excerptName, onResolve]);

	return null;
};

/**
 * Include Page (extensionKey 'include'/'include-page') support is gated on the
 * macro migration being active for the current tenant. The legacy include
 * renderer does not expose the included page's ADF to the client, so TOC
 * headings can only be resolved for the migrated `PageIncludeImpl`.
 *
 * When the migration is enabled, `PageIncludeImpl` resolves the included page
 * via `usePageIncludeMacroQuery` (Apollo, `cache-first`). We subscribe to the
 * exact same query with the exact same target so Apollo serves the already
 * cached result — no additional network request is fired for the TOC.
 */
const IncludePageTocSubscriber: FC<SubscriberProps> = ({ reference, onResolve }) => {
	const isMigrated = isMigratedMacroImplEnabled('include');

	// Rebuild the same parsed target the renderer uses so the query hits the
	// identical Apollo cache key. `reference.macroParams` is the raw macro
	// params object captured during reference extraction. When the migration is
	// off we deliberately produce a `missing-target` so the shared query hook
	// short-circuits and fires no request (legacy include has no client ADF).
	const parsedTarget = useMemo(
		() =>
			parseMacroTarget(
				isMigrated ? (reference.macroParams as PageIncludeMacroParams | undefined) : undefined,
			),
		[isMigrated, reference.macroParams],
	);

	// Pass the *host* page's space key (not the target's) as the second arg:
	// the query uses it only as the host-space fallback for space-less legacy
	// targets, and PageIncludeMacroRenderer passes macroDefaultProps.spaceKey
	// here too. Matching it keeps the Apollo cache key identical so we reuse the
	// renderer's cached result instead of firing a second request.
	const { adfString, loading, error } = usePageIncludeMacroQuery(
		parsedTarget,
		reference.hostSpaceKey,
	);

	useEffect(() => {
		if (!isMigrated) {
			onResolve({ key: reference.key, content: [] });
			return;
		}
		if (loading) {
			return;
		}
		if (error || !adfString) {
			onResolve({ key: reference.key, content: [] });
			return;
		}
		const parsedDocument = parseNestedAdfDocument(adfString);
		onResolve({
			key: reference.key,
			content: (parsedDocument.content ?? []) as ADFEntity[],
		});
	}, [isMigrated, adfString, loading, error, reference.key, onResolve]);

	return null;
};

type SubscribersProps = {
	references: IncludedContentReference[];
	onEntry: (entry: IncludedContentEntry) => void;
};

export const IncludedContentTocSubscribers: FC<SubscribersProps> = ({ references, onEntry }) => {
	return (
		<>
			{references.map((reference) => {
				if (reference.extensionKey === 'excerpt-include') {
					return (
						<ExcerptIncludeTocSubscriber
							key={reference.key}
							reference={reference}
							onResolve={onEntry}
						/>
					);
				}
				if (reference.extensionKey === 'include' || reference.extensionKey === 'include-page') {
					return (
						<IncludePageTocSubscriber
							key={reference.key}
							reference={reference}
							onResolve={onEntry}
						/>
					);
				}
				return null;
			})}
		</>
	);
};
